import React from 'react';
import { getCableColorForSVG, preprocessTextForWrapping, splitText } from '../../utils/cableLogic';
import { renderTerminalSchematic } from '../../utils/terminalRenderLogic';
import { COLOR_MAP } from '../../constants/colorConstants';
import CableSVGLine from './CableSVGLine';

// Funzione per verificare se è un cavo di terra
const isEarthWire = (colore) => {
    return colore === 'GI/VE' || colore === 'YE/GN';
};

// Funzione per verificare se una siglatura è valida (non placeholder)
const isValidSiglatura = (sig) => {
    if (!sig || String(sig).trim() === '') return false;
    const sigTrimmed = String(sig).trim();
    // Escludi placeholder e siglature di terra
    const invalidSigs = ['[Senza siglatura]', '[No marking]', 'TERRA', 'GND', 'EARTH'];
    return !invalidSigs.includes(sigTrimmed);
};

// Componente per visualizzazione cavi raggruppati in una guaina
const SheathCableVisualization = ({ cables, sheathLabel, hoveredCable, setHoveredCable, isMobile, settings, sheathType }) => {
    // Controllo che ci siano cavi
    if (!cables || cables.length === 0) {
        return null;
    }

    const svgWidth = isMobile ? 400 : 800;
    const svgHeight = Math.max(160, 40 + (cables.length * 50)); // Altezza dinamica ridotta
    const mainCableY = svgHeight / 2;

    // Dimensioni rettangolo guaina (centrale)
    const sheathWidth = 300;
    const sheathHeight = 40;
    const sheathX = (svgWidth - sheathWidth) / 2;
    const sheathRect = {
        x: sheathX,
        y: mainCableY - sheathHeight / 2,
        width: sheathWidth,
        height: sheathHeight
    };

    // Spazio per i cavi a sinistra e destra
    const cableLeftX = 120;
    const cableRightX = svgWidth - 120;
    const cableSpacing = Math.min(50, (svgHeight - 40) / Math.max(1, cables.length - 1));
    const cableStartY = svgHeight / 2 - ((cables.length - 1) * cableSpacing) / 2;

    const isBraided = sheathType === 'Trecciata';

    return (
        <svg
            className="w-full"
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            xmlns="http://www.w3.org/2000/svg"
        >
            <defs>
                <pattern id="braidPattern" patternUnits="userSpaceOnUse" width="30" height="30" patternTransform="rotate(45)">
                    <line x1="0" y1="0" x2="0" y2="30" stroke="white" strokeWidth="2" strokeOpacity="0.5" />
                    <line x1="0" y1="0" x2="30" y2="0" stroke="white" strokeWidth="2" strokeOpacity="0.5" />
                </pattern>
            </defs>

            {/* PRIMA: Rendering Cavi (sotto) */}
            {/* Calcola lunghezze massime per scala proporzionale (come nei multipolari) */}
            {(() => {
                const lunghezzeUniche = [...new Set(cables.map(c => c.lunghezza || 0))].filter(l => l > 0).sort((a, b) => a - b);
                const maxLunghezza = Math.max(...lunghezzeUniche);
                const maxScale = 80; // Lunghezza massima visiva in pixel

                return cables.map((cable, index) => {
                    const cableY = cableStartY + (index * cableSpacing);
                    const isHovered = hoveredCable === cable.originalIndex;

                    // ✅ CALCOLI IDENTICI AI MULTIPOLARI - LATO A (sinistra)
                    const cableExitXA = sheathRect.x;
                    const cableExitYA = sheathRect.y + (sheathRect.height / (cables.length + 1)) * (index + 1);

                    // Lunghezza proporzionale - IDENTICO AI MULTIPOLARI
                    const lunghezzaProporzionale = (cable.lunghezza || 0) / Math.max(maxLunghezza, 1);
                    const lunghezzaVisuale = lunghezzaProporzionale * maxScale;

                    const horizontalStartXA = cableLeftX + 40;
                    const horizontalEndXA = horizontalStartXA - lunghezzaVisuale; // Fine proporzionale

                    // ✅ CALCOLI IDENTICI AI MULTIPOLARI - LATO B (destra)
                    const cableExitXB = sheathRect.x + sheathRect.width;
                    const cableExitYB = sheathRect.y + (sheathRect.height / (cables.length + 1)) * (index + 1);

                    const horizontalStartXB = cableRightX - 40;
                    const horizontalEndXB = horizontalStartXB + lunghezzaVisuale; // Fine proporzionale

                    const opacity = hoveredCable !== null && !isHovered ? 0.3 : 1;

                    return (
                        <g key={`cable-${index}`}>
                            {/* LATO A (SINISTRA) */}
                            <g
                                onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                                onMouseLeave={() => setHoveredCable(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                {/* ✅ Linea obliqua dal rettangolo guaina verso il punto fisso */}
                                <CableSVGLine
                                    cable={cable}
                                    x1={cableExitXA}
                                    y1={cableExitYA}
                                    x2={horizontalStartXA}
                                    y2={cableY}
                                    isHovered={isHovered}
                                    useAWG={settings.useAWG}
                                    strokeLinecap="round"
                                    opacity={isHovered ? 1 : (hoveredCable !== null ? 0.3 : 1)}
                                />

                                {/* ✅ Tratto orizzontale */}
                                <CableSVGLine
                                    cable={cable}
                                    x1={horizontalStartXA}
                                    y1={cableY}
                                    x2={horizontalEndXA}
                                    y2={cableY}
                                    isHovered={isHovered}
                                    useAWG={settings.useAWG}
                                    strokeLinecap="round"
                                    opacity={isHovered ? 1 : (hoveredCable !== null ? 0.3 : 1)}
                                />

                                {/* ✅ Terminale A alla fine del tratto orizzontale */}
                                {renderTerminalSchematic(
                                    cable.terminaleA,
                                    horizontalEndXA,
                                    cableY,
                                    isMobile ? 0.03 : 0.04,
                                    'A',
                                    180
                                )}

                                {/* Codice terminale A */}
                                {cable.terminaleA && cable.terminaleA !== 'NS' && (
                                    <text
                                        x={horizontalEndXA}
                                        y={cableY + 20}
                                        className="text-xs font-medium fill-gray-700"
                                        textAnchor="middle"
                                    >
                                        {cable.terminaleA}
                                    </text>
                                )}

                                {/* Etichetta siglatura A - non mostrare per cavi di terra */}
                                {isValidSiglatura(cable.siglatura) && !isEarthWire(cable.colore) && (
                                    <g>
                                        <rect
                                            x={horizontalEndXA + 40 - 6}
                                            y={cableY - 8}
                                            width={(cable.siglatura.length * 8) + 12}
                                            height={16}
                                            rx={3}
                                            fill="white"
                                            stroke="#D1D5DB"
                                            strokeWidth={1}
                                            filter="drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))"
                                        />
                                        <text
                                            x={horizontalEndXA + 40}
                                            y={cableY + 4}
                                            className="text-xs font-bold fill-gray-800"
                                            textAnchor="start"
                                        >
                                            {cable.siglatura}
                                        </text>
                                    </g>
                                )}

                                {/* Collegamento A */}
                                {cable.collegamentoA && (
                                    <text
                                        x={horizontalEndXA - 30}
                                        y={cableY + 3}
                                        className="text-xs font-medium"
                                        textAnchor="end"
                                        fontSize="10"
                                        fill="#374151"
                                    >
                                        {splitText(preprocessTextForWrapping(cable.collegamentoA), 8).map((line, i) => (
                                            <tspan
                                                key={i}
                                                x={horizontalEndXA - 35}
                                                dy={i === 0 ? 0 : 12}
                                            >
                                                {line}
                                            </tspan>
                                        ))}
                                    </text>
                                )}
                            </g>

                            {/* LATO B (DESTRA) */}
                            <g
                                onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                                onMouseLeave={() => setHoveredCable(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                {/* ✅ Linea obliqua dal rettangolo guaina verso il punto fisso */}
                                <CableSVGLine
                                    cable={cable}
                                    x1={cableExitXB}
                                    y1={cableExitYB}
                                    x2={horizontalStartXB}
                                    y2={cableY}
                                    isHovered={isHovered}
                                    useAWG={settings.useAWG}
                                    strokeLinecap="round"
                                    opacity={isHovered ? 1 : (hoveredCable !== null ? 0.3 : 1)}
                                />

                                {/* ✅ Tratto orizzontale */}
                                <CableSVGLine
                                    cable={cable}
                                    x1={horizontalStartXB}
                                    y1={cableY}
                                    x2={horizontalEndXB}
                                    y2={cableY}
                                    isHovered={isHovered}
                                    useAWG={settings.useAWG}
                                    strokeLinecap="round"
                                    opacity={isHovered ? 1 : (hoveredCable !== null ? 0.3 : 1)}
                                />

                                {/* ✅ Terminale B alla fine del tratto orizzontale */}
                                {renderTerminalSchematic(
                                    cable.terminaleB,
                                    horizontalEndXB,
                                    cableY,
                                    isMobile ? 0.03 : 0.04,
                                    'B',
                                    0
                                )}

                                {/* Codice terminale B */}
                                {cable.terminaleB && cable.terminaleB !== 'NS' && (
                                    <text
                                        x={horizontalEndXB}
                                        y={cableY + 20}
                                        className="text-xs font-medium fill-gray-700"
                                        textAnchor="middle"
                                    >
                                        {cable.terminaleB}
                                    </text>
                                )}

                                {/* Etichetta siglatura B - non mostrare per cavi di terra */}
                                {isValidSiglatura(cable.siglatura) && !isEarthWire(cable.colore) && (
                                    <g>
                                        <rect
                                            x={horizontalEndXB - 40 - (cable.siglatura.length * 8) - 6}
                                            y={cableY - 8}
                                            width={(cable.siglatura.length * 8) + 12}
                                            height={16}
                                            rx={3}
                                            fill="white"
                                            stroke="#D1D5DB"
                                            strokeWidth={1}
                                            filter="drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))"
                                        />
                                        <text
                                            x={horizontalEndXB - 40}
                                            y={cableY + 4}
                                            className="text-xs font-bold fill-gray-800"
                                            textAnchor="end"
                                        >
                                            {cable.siglatura}
                                        </text>
                                    </g>
                                )}

                                {/* Collegamento B */}
                                {cable.collegamentoB && (
                                    <text
                                        x={horizontalEndXB + 30}
                                        y={cableY + 3}
                                        className="text-xs font-medium"
                                        textAnchor="start"
                                        fontSize="10"
                                        fill="#374151"
                                    >
                                        {splitText(preprocessTextForWrapping(cable.collegamentoB), 8).map((line, i) => (
                                            <tspan
                                                key={i}
                                                x={horizontalEndXB + 35}
                                                dy={i === 0 ? 0 : 12}
                                            >
                                                {line}
                                            </tspan>
                                        ))}
                                    </text>
                                )}
                            </g>
                        </g>
                    );
                });
            })()}

            {/* POI: Rettangolo centrale della guaina (sopra i cavi) */}
            <g>
                <rect
                    x={sheathRect.x}
                    y={sheathRect.y}
                    width={sheathRect.width}
                    height={sheathRect.height}
                    fill="#1f2937"
                    stroke="#111827"
                    strokeWidth="2"
                    rx="5"
                />

                {/* Pattern per guaina trecciata */}
                {isBraided && (
                    <rect
                        x={sheathRect.x}
                        y={sheathRect.y}
                        width={sheathRect.width}
                        height={sheathRect.height}
                        fill="url(#braidPattern)"
                        pointerEvents="none"
                        rx="5"
                        opacity={0.8}
                    />
                )}
            </g>

            {/* Etichetta guaina al centro */}
            <text
                x={sheathRect.x + sheathRect.width / 2}
                y={sheathRect.y + sheathRect.height / 2 + 5}
                textAnchor="middle"
                fontSize="14"
                fontWeight="600"
                fill="white"
            >
                {sheathLabel}
            </text>

            {/* Indici dei cavi sopra e sotto la guaina - DIVISI A METÀ */}
            {(() => {
                const indexSpacing = 40; // Spazio tra i cerchi
                const centerX = sheathRect.x + sheathRect.width / 2;

                // Dividi i cavi in due gruppi: primi 50% sopra, restanti sotto
                const splitIndex = Math.ceil(cables.length / 2);

                return cables.map((cable, index) => {
                    const isTop = index < splitIndex;

                    // Calcola posizione relativa nel sottogruppo per centrarli correttamente
                    const subGroupIndex = isTop ? index : (index - splitIndex);
                    const subGroupLength = isTop ? splitIndex : (cables.length - splitIndex);

                    // Centra il sottogruppo orizzontalmente rispetto alla guaina
                    const startX = centerX - ((subGroupLength - 1) * indexSpacing) / 2;
                    const indexX = startX + (subGroupIndex * indexSpacing);

                    // Posizione Y: sopra o sotto
                    // Sopra: y - 25 (come prima)
                    // Sotto: y + height + 25
                    const indexY = isTop
                        ? sheathRect.y - 25
                        : sheathRect.y + sheathRect.height + 25;

                    return (
                        <g key={`index-${index}`}>
                            <circle
                                cx={indexX}
                                cy={indexY}
                                r={15}
                                fill="#1F2937"
                                opacity={0.9}
                            />
                            <text
                                className="cable-number"
                                x={indexX}
                                y={indexY + 5}
                                fontSize={14}
                                fill="white"
                                textAnchor="middle"
                            >
                                #{cable.originalIndex + 1}
                            </text>
                        </g>
                    );
                });
            })()}
        </svg>
    );
};

export default SheathCableVisualization;

